import { alpha, Box, ButtonBase, makeStyles, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import React from "react";

import { Chevron } from "@/components/Chevron";
import { CashflowRoundingNote } from "@/components/management/CashflowRoundingNote";
import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import theme from "@/theme";

import { BalanceSummaryData } from "../utils/balanceSummary";

export const signedAmount = (value: number) => (value > 0 ? "+" : "") + value.toLocaleString();
const useStyles = makeStyles({
  columns: {
    display: "flex",
    flexDirection: "column",
    gap: layout.space[32],
    [theme.breakpoints.up("md")]: {
      display: "grid",
      gridTemplateColumns: "minmax(0,384fr) minmax(0,776fr)",
      gap: layout.space[40],
      alignItems: "start",
    },
  },
  trendCard: { boxShadow: "0 2px 4px " + alpha(colors["text/primary"], 0.06) },
  main: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: layout.space[32],
    [theme.breakpoints.up("md")]: { gridColumn: 2, gridRow: 1 },
  },
  side: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: layout.space[32],
    [theme.breakpoints.up("md")]: { gridColumn: 1, gridRow: 1, position: "sticky", top: 88 },
  },
  section: { display: "flex", flexDirection: "column", gap: layout.space[16], minWidth: 0 },
  heading: { ...textStyles["SP/Heading/H3"], [theme.breakpoints.up("md")]: textStyles["PC/Heading/H3"] },
  card: {
    background: colors["surface/card"],
    borderRadius: layout.radius.lg,
    minWidth: 0,
    padding: layout.space[16],
    [theme.breakpoints.up("md")]: { padding: layout.space[24] },
  },
  trendHead: {
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: layout.space[8],
    alignItems: "baseline",
  },
  trendTitle: {
    ...textStyles["SP/Heading/H4"],
    color: colors["text/heading"],
    [theme.breakpoints.up("md")]: textStyles["PC/Heading/H4"],
  },
  small: {
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Small"],
  },
  number: { ...textStyles["Number/S"], overflowWrap: "anywhere" },
  big: { ...textStyles["Number/XL"], color: colors["status/success"], overflowWrap: "anywhere" },
  amount: { display: "flex", alignItems: "baseline", gap: layout.space[4], flexWrap: "wrap" },
  trendAmount: {
    display: "flex",
    alignItems: "baseline",
    flexWrap: "wrap",
    gap: layout.space[12],
    marginTop: layout.space[16],
    marginBottom: layout.space[24],
  },
  plot: { width: "100%", height: 120, display: "block", [theme.breakpoints.up("md")]: { height: 160 } },
  axis: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: layout.space[8],
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
  },
  breakdown: {
    display: "flex",
    flexDirection: "column",
    gap: layout.space[24],
    [theme.breakpoints.up("md")]: { flexDirection: "row" },
  },
  group: { minWidth: 0, flex: 1 },
  divider: {
    height: 1,
    background: colors["border/default"],
    [theme.breakpoints.up("md")]: { width: 1, height: "auto", alignSelf: "stretch" },
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    gap: layout.space[8],
    alignItems: "baseline",
    marginTop: layout.space[8],
    ...textStyles["SP/Body/Large"],
    minWidth: 0,
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Small"],
  },
  rowName: { display: "flex", alignItems: "center", gap: layout.space[8], minWidth: 0, flex: 1 },
  swatch: { width: 8, height: 8, borderRadius: 2, flexShrink: 0 },
  percent: { ...textStyles["Number/XXS"], color: colors["text/secondary"], marginRight: layout.space[4] },
  bar: {
    display: "flex",
    gap: 2,
    height: 8,
    marginTop: layout.space[12],
    marginBottom: layout.space[16],
    overflow: "hidden",
    borderRadius: 2,
    background: colors["border/default"],
  },
  assetGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2,minmax(0,1fr))",
    gap: layout.space[12],
    marginBottom: layout.space[16],
  },
  progressHead: { display: "flex", justifyContent: "space-between", gap: layout.space[8], alignItems: "baseline" },
  progress: {
    height: 6,
    background: colors["border/default"],
    borderRadius: layout.radius.full,
    marginTop: layout.space[8],
    overflow: "hidden",
  },
  properties: { display: "flex", flexDirection: "column", gap: layout.space[8] },
  property: {
    width: "100%",
    textAlign: "left",
    background: colors["surface/card"],
    borderRadius: layout.radius.lg,
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    gap: layout.space[12],
    minWidth: 0,
    boxShadow: "0 2px 8px " + alpha(colors["text/primary"], 0.06),
    "&:focus-visible": { outline: "2px solid " + colors["border/focus"] },
  },
  propertyName: { ...textStyles["SP/Button/M"], whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  propertyText: { flex: 1, minWidth: 0 },
  propertyAddress: {
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginTop: layout.space[4],
  },
  propertyAmount: {
    display: "flex",
    alignItems: "baseline",
    flexShrink: 0,
    gap: layout.space[4],
    ...textStyles["Number/XS"],
  },
});

const amountColor = (amount: number) =>
  amount > 0 ? colors["status/success"] : amount < 0 ? colors["status/error"] : colors["text/primary"];
const Trend: React.FC<{ data: BalanceSummaryData }> = ({ data }) => {
  const classes = useStyles();
  const values = data.trend.filter((point) => point.amount != null).map((point) => point.amount as number);
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 0);
  const range = max - min || 1;
  const x = (index: number) => 8 + (index / 11) * 712;
  const y = (amount: number) => 8 + ((max - amount) / range) * 136;
  const segments: Array<Array<{ index: number; amount: number }>> = [];
  data.trend.forEach((point, index) => {
    if (point.amount == null) return;
    if (index === 0 || data.trend[index - 1].amount == null) segments.push([]);
    segments[segments.length - 1].push({ index, amount: point.amount });
  });
  return (
    <Box className={`${classes.card} ${classes.trendCard}`} data-balance-trend>
      <Box className={classes.trendHead}>
        <Typography component="h2" className={classes.trendTitle}>
          合計収支
        </Typography>
        <Typography className={classes.small}>直近12か月の推移</Typography>
      </Box>
      <Box className={classes.trendAmount}>
        <Box className={classes.amount}>
          <Typography component="span" className={classes.big} style={{ color: amountColor(data.totalCashFlow) }}>
            {signedAmount(data.totalCashFlow)}
          </Typography>
          <Typography component="span" className={classes.small}>
            円
          </Typography>
        </Box>
        {data.delta != null && <Typography className={classes.small}>前月比 {signedAmount(data.delta)}円</Typography>}
      </Box>
      <svg
        className={classes.plot}
        viewBox="0 0 728 160"
        preserveAspectRatio="none"
        role="img"
        aria-label={data.trend
          .map((p) => p.month + " " + (p.amount == null ? "未算出" : p.amount.toLocaleString() + "円"))
          .join("、")}
      >
        <defs>
          <linearGradient id="balance-trend-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colors["chart/income-1"]} stopOpacity={0.14} />
            <stop offset="100%" stopColor={colors["chart/income-1"]} stopOpacity={0} />
          </linearGradient>
        </defs>
        <line x1="0" x2="728" y1="159" y2="159" stroke={colors["border/default"]} />
        {segments.map((segment, index) => {
          const line = segment.map((p, i) => (i === 0 ? "M" : "L") + x(p.index) + "," + y(p.amount)).join(" ");
          return (
            <g key={index}>
              <path
                d={line + " L" + x(segment[segment.length - 1].index) + ",160 L" + x(segment[0].index) + ",160 Z"}
                fill="url(#balance-trend-fill)"
              />
              <path
                d={line}
                fill="none"
                stroke={colors["chart/income-1"]}
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
              />
            </g>
          );
        })}
        {data.trend.map((point, index) =>
          point.amount == null ? null : (
            <circle
              key={point.month}
              cx={x(index)}
              cy={y(point.amount)}
              r={index === 11 ? 3 : 0}
              fill={colors["chart/income-1"]}
            >
              <title>
                {point.month} {point.amount.toLocaleString()}円
              </title>
            </circle>
          )
        )}
      </svg>
      <Box className={classes.axis}>
        {[0, 3, 6, 9, 11].map((index) => (
          <span key={index}>
            {index === 11 && data.graphEnd.isSame(dayjs(), "month")
              ? "今月"
              : dayjs(data.trend[index].month).format("M月")}
          </span>
        ))}
      </Box>
    </Box>
  );
};

type Entry = { name: string; value: number; color: string };
const BreakdownGroup: React.FC<{ title: string; total: number; items: Entry[] }> = ({ title, total, items }) => {
  const classes = useStyles();
  const positiveSum = items.reduce((sum, item) => sum + Math.max(0, item.value), 0);
  return (
    <Box className={classes.group}>
      <Box className={classes.progressHead}>
        <Typography className={classes.trendTitle}>{title}</Typography>
        <Box className={classes.amount}>
          <Typography className={classes.number}>{total.toLocaleString()}</Typography>
          <Typography className={classes.small}>円</Typography>
        </Box>
      </Box>
      <Box className={classes.bar} aria-hidden>
        {items.map((item) => (
          <span
            key={item.name}
            style={{
              background: item.color,
              width: (positiveSum > 0 ? (Math.max(0, item.value) / positiveSum) * 100 : 0) + "%",
            }}
          />
        ))}
      </Box>
      {items.map((item) => (
        <Box className={classes.row} key={item.name}>
          <Box className={classes.rowName}>
            <span className={classes.swatch} style={{ background: item.color }} />
            <span>{item.name}</span>
          </Box>
          <Box className={classes.amount}>
            {positiveSum > 0 && item.value >= 0 && (
              <span className={classes.percent}>{((item.value / positiveSum) * 100).toFixed(1)}%</span>
            )}
            <span>{Math.round(item.value).toLocaleString()}</span>
            <span className={classes.small}>円</span>
          </Box>
        </Box>
      ))}
    </Box>
  );
};
export const BalanceOverview: React.FC<{ data: BalanceSummaryData; onSelect: (id: number) => void }> = ({
  data,
  onSelect,
}) => {
  const classes = useStyles();
  const a = data.amounts;
  const income = [
    { name: "家賃収入", value: a.rentIncome, color: colors["chart/income-1"] },
    { name: "管理費収入", value: a.managementFeeIncomeOnly, color: colors["chart/income-2"] },
    { name: "その他収入", value: a.otherIncome, color: colors["chart/other"] },
  ];
  const expenses: Entry[] = [
    { name: "ローン返済", value: a.loanRepayment, color: colors["chart/expense-1"] },
    { name: "建物管理費", value: a.buildingManagementCost, color: colors["chart/expense-2"] },
    { name: "修繕積立金", value: a.repairFundCost, color: colors["chart/expense-3"] },
    { name: "賃貸管理費", value: a.rentalManagementCost, color: colors["chart/expense-4"] },
    ...(data.hasBuilding
      ? [
          { name: "管理会社委託費", value: a.buildingManagementCompanyCommission, color: colors["chart/expense-2"] },
          { name: "共用部水道光熱費", value: a.buildingSharedUtilityExpenses, color: colors["chart/expense-3"] },
        ]
      : []),
    { name: "その他", value: a.miscCost + a.communicationCost + a.buildingMiscCost, color: colors["chart/other"] },
  ];
  return (
    <Box className={classes.columns}>
      <Box className={classes.main}>
        <Trend data={data} />
        <Box className={classes.section}>
          <Typography component="h2" className={classes.heading}>
            収支の内訳
          </Typography>
          <Box className={classes.card}>
            <Box className={classes.breakdown}>
              <BreakdownGroup title="収入" total={data.income} items={income} />
              <Box className={classes.divider} />
              <BreakdownGroup title="支出" total={data.expense} items={expenses} />
            </Box>
          </Box>
          <Typography className={classes.small}>
            固定資産税・保険料・一時的な収支・購入時費用は、下の「詳しい収支」で確認できます。
          </Typography>
          <CashflowRoundingNote />
        </Box>
      </Box>
      <Box className={classes.side}>
        <Box className={classes.section}>
          <Typography component="h2" className={classes.heading}>
            資産とローン
          </Typography>
          <Box className={classes.card}>
            <Box className={classes.assetGrid}>
              {[
                ["総資産", data.assets],
                ["ローン残債", data.loanRemaining],
              ].map(([name, value]) => (
                <Box key={String(name)}>
                  <Typography className={classes.small}>{name}</Typography>
                  <Box className={classes.amount}>
                    <Typography className={classes.number}>
                      {value == null ? "未算出" : Number(value).toLocaleString()}
                    </Typography>
                    {value != null && <Typography className={classes.small}>円</Typography>}
                  </Box>
                </Box>
              ))}
            </Box>
            {data.loanProgress != null && (
              <>
                <Box className={classes.progressHead}>
                  <Typography className={classes.small}>ローン返済の進み具合</Typography>
                  <Typography style={textStyles["Number/XXS"]}>{data.loanProgress.toFixed(1)}%</Typography>
                </Box>
                <Box
                  className={classes.progress}
                  role="progressbar"
                  aria-label="ローン返済の進み具合"
                  aria-valuenow={Math.round(data.loanProgress)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <Box height="100%" width={data.loanProgress + "%"} bgcolor={colors["action/primary"]} />
                </Box>
              </>
            )}
            <Typography className={classes.small} style={{ marginTop: layout.space[12] }}>
              購入価格の合計と今月の残債です。
            </Typography>
          </Box>
        </Box>
        <Box className={classes.section}>
          <Typography component="h2" className={classes.heading}>
            物件ごとの収支
          </Typography>
          <Box className={classes.properties}>
            {data.perProperty.map(({ property, amount }) => (
              <ButtonBase
                key={property.id}
                className={classes.property}
                onClick={() => onSelect(property.id)}
                aria-label={property.property.name + "に絞り込む"}
              >
                <Box className={classes.propertyText}>
                  <Typography className={classes.propertyName}>{property.property.name}</Typography>
                  <Typography className={classes.propertyAddress}>
                    {[property.property.prefecture, property.property.city, property.property.town]
                      .filter(Boolean)
                      .join("")}
                  </Typography>
                </Box>
                <Box
                  className={classes.propertyAmount}
                  style={{ color: amount == null ? colors["text/secondary"] : amountColor(amount) }}
                >
                  <span>{amount == null ? "未算出" : signedAmount(amount)}</span>
                  {amount != null && <span className={classes.small}>円</span>}
                  <Chevron style={{ color: colors["icon/muted"] }} />
                </Box>
              </ButtonBase>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
